// src/i18n/resolveLanguage.js
//
// The one rule for which language the site is shown in (Roadmap #85,
// docs/briefs/2026-10-01-country-default-language.md + addenda 1 and 2).
// Pure and synchronous: it holds no state and makes no request.
//
// R2′: a person's explicit choice wins everywhere, while that language is offered. With no
// choice (or a withdrawn one), the default of the country in view applies (R1). The browser's
// language is never consulted. A country with no default falls back to English (Architect,
// 1 Oct 2026) — English is also what the translation system itself falls back to.

export const FALLBACK_LOCALE = 'en';

/**
 * @param {object} args
 * @param {string|null} args.choice - the person's explicit choice, or null
 * @param {object|null|undefined} args.settings - { languages: [{ locale }], countryDefaults: { PT: 'pt-PT' } };
 *   undefined while still loading, null when the read failed
 * @param {string|null} args.countryCode - the country in view
 * @returns {string|null} the locale, or null while the settings are still loading and no choice is known
 */
export function resolveLocale({ choice = null, settings, countryCode = null }) {
  // Still loading: undecided, unless the person has already chosen.
  if (settings === undefined) return choice || null;
  // The read failed: the list is unknown, not empty — keep the choice rather than refuse it.
  if (settings === null) return choice || FALLBACK_LOCALE;

  const offered = new Set((settings.languages || []).map(l => l.locale));
  if (choice && offered.has(choice)) return choice;

  const countryDefault = countryCode ? settings.countryDefaults?.[countryCode] : null;
  if (countryDefault && offered.has(countryDefault)) return countryDefault;

  return FALLBACK_LOCALE;
}

/**
 * The locale dates and numbers are formatted in, from the resolved language (A4).
 * English is shown as British English; every other language formats in its own code.
 * @param {string|null} locale
 * @returns {string}
 */
export function toFormattingLocale(locale) {
  if (!locale || locale === 'en') return 'en-GB';
  return locale;
}
